import type {
  MentionEntity,
  ObjectContextMentionData,
  SkillMentionData,
  TerraUIMessage,
  TerraUIMessagePart,
} from '@terra/shared-dto-chat-types';
import {
  DataMessageTypeEnum,
  TerraUIMessageDataPartName,
} from '@terra/shared-dto-chat-types';
import type {
  ChatMentionsMenuItemType,
  ChatRef,
  MentionEntityWithOffset,
} from '@terra/ui-design-system';
import {
  buildBranchResendMessageParts,
  buildMentionDisplaySegments,
  collectMentionEntitiesForSend,
  extractRawTextFromMention,
  hasRichCardRenderer,
} from '@terra/ui-design-system';
import {
  composerContextAfterSend,
  selectMentionsForSend,
} from './composerSendContext';
import { useChatTabsStore } from '../store/chatTabsStore';
import { isMongoObjectId } from '@terra/shared-utils';
import {
  EMPTY_SKILL_DRAFT,
  useSkillDraftsStore,
} from '../store/skillDraftsStore';
import { formatSkillMentionRawText } from '../utils/skillMentionUtils';
import { formatFindingLinkedObjectRawText } from '../utils/findingLinkedContextUtils';
import { readObjectValue } from '../utils/readObjectValue';
import type { useCopilotChatBranching } from './useCopilotChatBranching';

export type BranchEditSource = ReturnType<
  typeof useCopilotChatBranching
>['branchEditSource'];

function reportInvalidMention({
  entityType,
  hasEntityId,
}: {
  entityType: string | undefined;
  hasEntityId: boolean;
}) {
  if (typeof globalThis.reportError !== 'function') {
    return;
  }

  globalThis.reportError(
    new Error(
      `Message contains invalid mention metadata: entityType=${entityType ?? 'unknown'}, hasEntityId=${String(hasEntityId)}`,
    ),
  );
}

function isSkillMode(value: unknown): value is SkillMentionData['mode'] {
  return value === 'create' || value === 'edit';
}

function getSkillMentionData(
  mentionEntity: MentionEntity,
): SkillMentionData | undefined {
  const skillData = mentionEntity.extraData?.skill;

  if (skillData && typeof skillData === 'object') {
    const skillDraftId = readObjectValue(skillData, 'skillDraftId');
    const skillId = readObjectValue(skillData, 'skillId');
    const mode = readObjectValue(skillData, 'mode');

    if (typeof skillDraftId === 'string' && isSkillMode(mode)) {
      return {
        skillDraftId,
        skillId: typeof skillId === 'string' ? skillId : undefined,
        mode,
      };
    }
  }

  if (
    typeof mentionEntity.extraData?.skillDraftId === 'string' &&
    isSkillMode(mentionEntity.extraData.mode)
  ) {
    return {
      skillDraftId: mentionEntity.extraData.skillDraftId,
      skillId:
        typeof mentionEntity.extraData.skillId === 'string'
          ? mentionEntity.extraData.skillId
          : undefined,
      mode: mentionEntity.extraData.mode,
    };
  }

  return undefined;
}

function createSkillMentionExtraData(skillData: SkillMentionData) {
  return {
    skill: skillData,
    skillDraftId: skillData.skillDraftId,
    skillId: skillData.skillId,
    mode: skillData.mode,
  };
}

function getSkillMentionDataFromDraftId(draftId: string): SkillMentionData {
  const editPrefix = 'edit-';
  const isEditDraft = draftId.startsWith(editPrefix);

  return {
    skillDraftId: draftId,
    skillId: isEditDraft ? draftId.slice(editPrefix.length) : undefined,
    mode: isEditDraft ? 'edit' : 'create',
  };
}

function resolveSkillMentionRawText({
  skillData,
  title,
  untrustedDataNotice,
}: {
  skillData: SkillMentionData;
  title: string;
  untrustedDataNotice: string;
}): string {
  const draft =
    useSkillDraftsStore.getState().getDraft(skillData.skillDraftId) ??
    EMPTY_SKILL_DRAFT;

  return formatSkillMentionRawText({
    mode: skillData.mode,
    skillId: skillData.skillId,
    skillDraftId: skillData.skillDraftId,
    draft,
    title,
    untrustedDataNotice,
  });
}

export function buildChatInstanceOutgoingParts({
  message,
  activeBranchEditSource,
  inlinePositionedMentions,
  chatId,
  isInlineBranchEdit,
  isChatWindowActive,
  selectedMentions,
  clearMentions,
  getMentionsFromEditor,
  chatComponentRef,
  updateChatTab,
  skillDraftId,
  linkedObject,
  tCopilot,
}: {
  message: TerraUIMessage;
  activeBranchEditSource?: NonNullable<BranchEditSource>;
  inlinePositionedMentions?: MentionEntityWithOffset[];
  chatId: string;
  isInlineBranchEdit: boolean;
  isChatWindowActive: boolean;
  selectedMentions: ChatMentionsMenuItemType[] | undefined;
  clearMentions: () => void;
  getMentionsFromEditor: () => MentionEntityWithOffset[];
  chatComponentRef?: React.RefObject<ChatRef | null>;
  updateChatTab: (
    tabId: string,
    updates: {
      mentions?: ChatMentionsMenuItemType[];
      linkedObject?: { kind: string; id: string; label?: string };
    },
  ) => void;
  skillDraftId?: string;
  linkedObject?: { kind: string; id: string; label?: string };
  tCopilot: (key: string) => string;
}): {
  content: string;
  originalUserText: string;
  mentions: MentionEntity[];
  conversationAttachmentsPart: TerraUIMessagePart | undefined;
  messageParts: TerraUIMessagePart[];
  clearSentComposerContext: () => void;
  preservedBranchAttachmentCount: number;
} {
  const textPart = message.parts?.find((part) => part.type === 'text');
  let content = textPart?.text?.trim() || '';
  const originalUserText = content;
  const conversationAttachmentsPart = message.parts?.find(
    (part) =>
      part.type === TerraUIMessageDataPartName &&
      part.data?.type === DataMessageTypeEnum.ConversationAttachments,
  );
  const richCardRawTexts: string[] = [];
  const resolvedSkillDraftIds = new Set<string>();
  const rawTextForMentions = textPart?.text ?? '';
  const leadingTrimOffset =
    rawTextForMentions.length - rawTextForMentions.trimStart().length;
  const positionedMentions =
    inlinePositionedMentions ?? getMentionsFromEditor();
  const adjustedPositionedMentions =
    leadingTrimOffset > 0
      ? positionedMentions.map((mention) => ({
          ...mention,
          offset: mention.offset - leadingTrimOffset,
        }))
      : positionedMentions;

  const chatTab = useChatTabsStore
    .getState()
    .chatTabs.find((tab) => tab.chatId === chatId);
  const clearSentComposerContext = () => {
    if (isInlineBranchEdit) {
      return;
    }

    const { clearLiveMentions } = composerContextAfterSend({
      isChatWindowActive,
    });

    if (clearLiveMentions) {
      chatComponentRef?.current?.clearInput();
      clearMentions();
    }

    if (chatTab?.mentions?.length) {
      updateChatTab(chatTab.tabId, { mentions: [] });
    }
  };

  const mentionSelection = isInlineBranchEdit
    ? []
    : selectMentionsForSend({
        isChatWindowActive,
        liveMentions: selectedMentions ?? [],
        chatTabMentions: chatTab?.mentions ?? [],
      });

  for (const { mentionEntity } of mentionSelection) {
    if (
      mentionEntity &&
      (!mentionEntity.entityId || !mentionEntity.entityType)
    ) {
      reportInvalidMention({
        entityType: mentionEntity.entityType,
        hasEntityId: Boolean(mentionEntity.entityId),
      });
    }
  }

  let mentions = collectMentionEntitiesForSend({
    selectedMentions: mentionSelection,
    positionedMentions: adjustedPositionedMentions,
  });

  mentions = mentions.map((mention) => {
    if (mention.entityType !== 'Skill') {
      return mention;
    }

    const skillData = getSkillMentionData(mention);

    if (!skillData) {
      return mention;
    }

    return {
      ...mention,
      extraData: createSkillMentionExtraData(skillData),
    };
  });

  for (const mention of mentions) {
    if (mention.entityType === 'Skill') {
      const skillData = getSkillMentionData(mention);

      if (!skillData) {
        continue;
      }

      resolvedSkillDraftIds.add(skillData.skillDraftId);
      richCardRawTexts.push(
        resolveSkillMentionRawText({
          skillData,
          title: tCopilot('skillContextTitle'),
          untrustedDataNotice: tCopilot('skillContextUntrustedDataNotice'),
        }),
      );
      continue;
    }

    if (!hasRichCardRenderer(mention.entityType)) {
      continue;
    }

    const rawText = extractRawTextFromMention(mention);

    if (rawText) {
      richCardRawTexts.push(rawText);
    }
  }

  if (skillDraftId && !resolvedSkillDraftIds.has(skillDraftId)) {
    const skillData = getSkillMentionDataFromDraftId(skillDraftId);

    richCardRawTexts.push(
      resolveSkillMentionRawText({
        skillData,
        title: tCopilot('skillContextTitle'),
        untrustedDataNotice: tCopilot('skillContextUntrustedDataNotice'),
      }),
    );
  }

  if (linkedObject?.kind === 'Finding') {
    richCardRawTexts.push(
      formatFindingLinkedObjectRawText({
        findingId: linkedObject.id,
        label: linkedObject.label,
        title: tCopilot('findingContextTitle'),
        untrustedDataNotice: tCopilot('findingContextUntrustedDataNotice'),
      }),
    );
  }

  const objectContextFinding = mentions.find((mention) => {
    if (mention.entityType !== 'ObjectContext') {
      return false;
    }

    const objectContext = mention.extraData?.objectContext as
      | ObjectContextMentionData
      | undefined;

    return (
      objectContext?.objectKind === 'Finding' &&
      isMongoObjectId(objectContext.objectId)
    );
  });
  const objectContextFindingData = objectContextFinding?.extraData
    ?.objectContext as ObjectContextMentionData | undefined;

  if (objectContextFindingData) {
    const tabForChat = useChatTabsStore
      .getState()
      .chatTabs.find((tab) => tab.chatId === chatId);

    if (tabForChat?.tabId) {
      updateChatTab(tabForChat.tabId, {
        linkedObject: {
          kind: 'Finding',
          id: objectContextFindingData.objectId,
          label: objectContextFindingData.objectLabel,
        },
      });
    }
  }

  if (richCardRawTexts.length > 0) {
    const rawTextContent = richCardRawTexts
      .map(
        (rawText) =>
          '<!-- TERRA_MENTION_START -->\n```\n' +
          rawText +
          '\n```\n<!-- TERRA_MENTION_END -->',
      )
      .join('\n\n');

    content = content ? `${content}\n\n${rawTextContent}` : rawTextContent;
  }

  const composerBranchEditSource =
    activeBranchEditSource?.kind === 'composer' ? activeBranchEditSource : null;
  const preservedBranchAttachmentCount =
    composerBranchEditSource?.attachmentPartIndexes.length ?? 0;

  const messageParts: TerraUIMessagePart[] = composerBranchEditSource
    ? buildBranchResendMessageParts({
        source: composerBranchEditSource,
        text: content,
        mentions,
        positionedMentions: adjustedPositionedMentions,
        buildSegments: (plainText, positioned) =>
          buildMentionDisplaySegments({
            plainText,
            positionedMentions: positioned,
            fallbackMentions: mentions.map((mention) => ({
              entityId: mention.entityId,
              entityType: mention.entityType,
              label: mention.label,
            })),
          }),
        newAttachmentParts: conversationAttachmentsPart
          ? [conversationAttachmentsPart]
          : [],
      })
    : [];

  if (!composerBranchEditSource) {
    if (content) {
      messageParts.push({ type: 'text', text: content });
    }

    if (mentions.length > 0) {
      const segments = buildMentionDisplaySegments({
        plainText: rawTextForMentions.trim(),
        positionedMentions: adjustedPositionedMentions,
        fallbackMentions: mentions.map((mention) => ({
          entityId: mention.entityId,
          entityType: mention.entityType,
          label: mention.label,
        })),
      });

      messageParts.push({
        type: TerraUIMessageDataPartName,
        data: {
          type: DataMessageTypeEnum.Mentions,
          mentions,
          ...(segments.length > 0 ? { segments } : {}),
        },
      });
    }

    if (conversationAttachmentsPart) {
      messageParts.push(conversationAttachmentsPart);
    }
  }

  return {
    content,
    originalUserText,
    mentions,
    conversationAttachmentsPart,
    messageParts,
    clearSentComposerContext,
    preservedBranchAttachmentCount,
  };
}
